import { PAGE_TYPE } from '@/app/common/pageType.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import { startServerSideTiming } from '@/app/components/search/utils/timings/timings.ts';
import { isEmptyObject } from '@/app/components/search/Utilities.ts';
import { createSearchParamsFromUrl } from '@/app/common/createSearchParamsFromUrl.ts';
import { isDestinationsPageFromSearchParams } from '@/app/components/search/utils/searchTypesUtils.ts';
import { SEARCH_TYPE } from '@/app/search/types.ts';
import { removeParameters, updateUrl } from '@/app/common/urlParametersUtils.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import { createResultsParamsToDrop } from '@/app/common/createResultsParamsToDrop.ts';
import { shouldRefreshPageWrapperData } from '@/app/common/shouldRefreshPageWrapperData.ts';
import { BASE_PATH } from '@/app/components/search/utils/path.ts';
import { createQueryString } from '@/utils/queryUtils.ts';
import { addBrowserHistory, replaceBrowserHistory } from '@/app/common/browserHistory.ts';
import type { PageWrapperDataPayload } from '@/app/api/pageWrapperData/route.ts';
import clientAPICall from '@/app/components/search/common/clientAPICall.ts';
import type { PageWrapperData } from '@/app/common/createPageWrapperData.ts';
import { API_NAME } from '@/app/components/search/common/apiTypes.ts';
import { startTransition } from 'react';
import { createSearchDefaultData } from '@/app/search/createSearchDefaultData.ts';
import type { SearchPageData } from '@/app/search/createSearchPageData.ts';
import type { SearchPageDataPayload } from '@/app/api/search/searchPageData/route.ts';
import handleMapLoad from '@/app/components/search/common/reloadData/handleMapLoad.ts';
import { ReloadType } from '@/app/components/search/utils';
import { FORCE_CONFIG_URL_PARAM_PREFIX } from '@lmn/lmn-commonlogic/dist/config/configuration';
import { SOURCE } from '@/app/components/search/app';
import { createPlatformSearchId } from '@/utils/searchIdUtils.ts';
import { createSubSourceForNewSearch } from '@/app/common/starTrack/events/searchRequested.ts';
import type {
  CreatePageWrapperClientSearchProps,
  FireSearchEventsAfterProps,
  FireSearchEventsBeforeProps,
  HandleDynamicFiltersResponseChangedProps,
  HandleNewSearch,
  HandleSearchChanged,
  HandleSearchChangedBaseProps,
  HandleSearchChangedProps,
  HandleSearchProductsDataChanged,
} from '@/app/common/PageWrapperClientTypes.tsx';
import { APIStatusEnum } from '@/app/components/search/common/reloadData/APIStatusEnum.ts';
import type { HandleWaitingFinishedParams } from '@/app/components/search/common/waitingCall.ts';
import { waitingCall } from '@/app/components/search/common/waitingCall.ts';
import type { ProductsData } from '@/app/api/search/products/createProductsData.ts';
import {
  fireSearchDestinationsEventsAfter,
  fireSearchDestinationsEventsBefore,
  fireSearchProductsEventsAfter,
  fireSearchProductsEventsBefore,
} from '@/app/common/PageWrapperClientEventsUtils.tsx';
import getSearchParams from '@/app/components/search/common/getSearchParams.ts';
import { isFalsyOrEmpty } from '@/app/common/utils/utility.ts';
import { SearchModes } from '@/domainLogic/index.ts';
import { EXPERIMENT_NAMES } from '@/app/common/ABTesting/ABTestingVariants.ts';
import { clientAppLog } from '@/app/components/search/logging/clientAppLog.ts';
import {
  appendSearchLoadingStage,
  setSearchLoadingStage,
  watchSearchDataReady,
} from '@/app/components/search/logging/searchLoadingWatchdog.ts';
import type { FiltersGroups } from '@/app/common/filters/core/filtersTypes.ts';
import { buildFilterGroups } from '@/app/common/filters/groups/buildFilterGroups.ts';
import { dynamicFiltersSearchKey } from '@/app/common/filters/core/dynamicFiltersSearchKey.ts';

let latestCallId = 0;

// The CSW caches the handlers from its first render: its zustand store is created there and never
// refreshed, because useStoreSync compares tab configs with JSON.stringify, which cannot see functions.
// On a slow first load that render is one React never committed, so a search fired from the widget wrote
// its loading state, and later its results, into a fiber that is not in the tree — React dropped every
// one of them and the placeholders stayed up for ever. The mounted component republishes its handler
// here after each commit, and the copy the widget cached delegates to it. (MPH-749)
let liveHandleNewSearch: HandleNewSearch | undefined;

export const publishHandleNewSearch = (handler: HandleNewSearch) => {
  liveHandleNewSearch = handler;
};

export const handleNewSearchLive: HandleNewSearch = (newSearchParams, shouldAddBrowserHistory) => {
  if (!liveHandleNewSearch) {
    clientAppLog({
      level: 'WARN',
      codeSourceName: 'pageWrapperClientSearch',
      logMessage: `handleNewSearchLive called before a mounted handler was published, search dropped. searchParams:${JSON.stringify(newSearchParams)}`,
      url: window?.location?.href,
    });
    return;
  }

  liveHandleNewSearch(newSearchParams, shouldAddBrowserHistory);
};

const scrollTo = (scrollY: number) => {
  setTimeout(() => {
    window.scrollTo(0, scrollY);
  }, 10);
};

const createOldParamsToKeep = (paramNames: string[], oldSearchParams: SearchParams) =>
  paramNames.reduce(
    (result, paramName: string) =>
      oldSearchParams[paramName] ? { ...result, [paramName]: oldSearchParams[paramName] } : result,
    {}
  );

const paramNameContainsAbTestName = (paramName: string): boolean =>
  Object.values(EXPERIMENT_NAMES).some((experimentName: string) => paramName.indexOf(experimentName) > -1);

type EnhanceNewSearchParams = {
  oldSearchParams: SearchParams;
  newSearchParams: SearchParams;
  newSearchType: SEARCH_TYPE;
  newPageType: PAGE_TYPE;
  userId: string;
};
const enhanceNewSearchParams = ({
  oldSearchParams,
  newSearchParams,
  newSearchType,
  newPageType,
  userId,
}: EnhanceNewSearchParams): SearchParams => {
  const configParams = Object.keys(oldSearchParams)
    .filter(
      (paramName) => paramName.startsWith(FORCE_CONFIG_URL_PARAM_PREFIX) || paramNameContainsAbTestName(paramName)
    )
    .reduce((acc: Record<string, string | undefined>, key: string) => {
      acc[key] = oldSearchParams[key];
      return acc;
    }, {});

  return {
    ...createOldParamsToKeep(
      [
        URLParameters.BUSINESS_PROFILE_ID,
        URLParameters.PINK_PEOPLE,
        URLParameters.DEBUG,
        URLParameters.HOST_NAME,
        URLParameters.ALLOW_DESKTOP,
        URLParameters.SEED,
        URLParameters.VIEW_MODE_DESKTOP,
        URLParameters.APP,
        URLParameters.APPVIEW,
      ],
      oldSearchParams
    ),
    [URLParameters.SOURCE]:
      newSearchType === SEARCH_TYPE.DESTINATIONS ? SOURCE.WIDGET_TSX_INDEX : SOURCE.WIDGET_TSX_MAP,
    [URLParameters.SEARCH_ID]: createPlatformSearchId(userId),
    [URLParameters.BF_SUB_SOURCE]: createSubSourceForNewSearch({ oldSearchParams, newPageType }),
    ...configParams,
    ...newSearchParams,
    // Apply the same origin default as the middleware (urlParamsValidations.ts) for DP searches.
    // The middleware only runs on full page loads, not on client-side navigations triggered by the
    // search widget. Without this fallback, SEARCH_REQUESTED would fire with departure=null whenever
    // the widget omits origin in a DP or TH search.
    ...(!newSearchParams[URLParameters.ORIGIN] &&
    (!newSearchParams[URLParameters.SEARCH_MODE] || newSearchParams[URLParameters.SEARCH_MODE] === SearchModes.DP)
      ? { [URLParameters.ORIGIN]: 'LON' }
      : {}),
  };
};

export const createPageWrapperClientSearch = ({
  commonData,
  pageWrapperData,
  searchPageData,
  searchDefaultData,
  localization,
  scrollPosSearch,
  mapRef,
  isMobile,
  translationDictionary,
  starTrack,
  createUpdatedDefaultValues,
  scrollToTop,
  setDefaultValuesCSW,
  setPageType,
  setPageWrapperData,
  setSearchPageData,
  setHotelId,
  getListView,
}: CreatePageWrapperClientSearchProps) => {
  const fireSearchEventsBefore = ({
    searchParams,
    searchInfoData,
    searchInfoDetailData,
    localization: localizationToUse,
    commonData: commonDataToUse,
    curiosityParams: curiosityParamsToUse,
  }: FireSearchEventsBeforeProps) => {
    if (searchInfoData.searchType === SEARCH_TYPE.PRODUCTS) {
      fireSearchProductsEventsBefore({
        searchParams,
        localization: localizationToUse,
        commonData: commonDataToUse,
        searchInfoData,
        searchInfoDetailData,
        starTrack,
      });
    }

    if (searchInfoData.searchType === SEARCH_TYPE.DESTINATIONS) {
      fireSearchDestinationsEventsBefore({
        searchParams,
        localization: localizationToUse,
        commonData: commonDataToUse,
        curiosityParams: curiosityParamsToUse,
        searchInfoData,
        searchInfoDetailData,
        starTrack,
      });
    }
  };

  const fireSearchEventsAfter = ({
    isNewSearch,
    searchParams,
    productsData,
    searchInfoData,
    searchInfoDetailData,
    commonData: commonDataToUse,
    localization: localizationToUse,
    curiosityParams: curiosityParamsToUse,
    defaultDestItems,
  }: FireSearchEventsAfterProps) => {
    if (searchInfoData.searchType === SEARCH_TYPE.PRODUCTS) {
      fireSearchProductsEventsAfter({
        listView: getListView(),
        isNewSearch,
        searchParams,
        productsData,
        searchInfoData,
        searchInfoDetailData,
        defaultDestItems,
        commonData: commonDataToUse,
        localization: localizationToUse,
        curiosityParams: curiosityParamsToUse,
        starTrack,
      });
    }

    if (searchInfoData.searchType === SEARCH_TYPE.DESTINATIONS) {
      fireSearchDestinationsEventsAfter({
        searchParams,
        productsData,
        searchInfoData,
        searchInfoDetailData,
        defaultDestItems,
        commonData: commonDataToUse,
        localization: localizationToUse,
        curiosityParams: curiosityParamsToUse,
        starTrack,
      });
    }
  };

  const handleSearchChangedBase = async ({
    newPageType,
    newSearchParams,
    isNewSearch = false,
    refreshCSW = true,
    refreshSearch,
    scrollPos,
    shouldAddBrowserHistory,
    highlightedHotel,
    isChangeListView,
    isBrowserBackNavigation = false,
    isAlternativeDates = false,
  }: HandleSearchChangedBaseProps) => {
    latestCallId += 1;
    const myCallId = latestCallId;
    const { userUid } = commonData;
    const { searchInfoData, searchInfoDetailData } = pageWrapperData;

    const timings = startServerSideTiming();

    // The same question is asked three times below - whether this navigation ends in a fresh
    // request for the results - so it is decided once here.
    const willRedoSearch = refreshSearch || isEmptyObject(searchPageData?.productsData);

    if (willRedoSearch) {
      scrollToTop();
      setSearchLoadingStage(isNewSearch ? 'newSearch' : 'searchChanged');
      handleIsLoadingChanged(true);
    } else {
      scrollTo(scrollPos !== undefined ? scrollPos : scrollPosSearch.current);
    }

    const oldSearchParams = createSearchParamsFromUrl();
    const isDestinations = isDestinationsPageFromSearchParams({
      searchParams: newSearchParams || oldSearchParams,
      geodbLiveCountrySearch: localization.configValuesApp.geodbLiveCountrySearch,
    });
    const newSearchType = isDestinations ? SEARCH_TYPE.DESTINATIONS : SEARCH_TYPE.PRODUCTS;
    const newPageTypeToUse =
      newPageType === PAGE_TYPE.SEARCH_DESTINATIONS || newSearchType === SEARCH_TYPE.DESTINATIONS
        ? PAGE_TYPE.SEARCH_DESTINATIONS
        : PAGE_TYPE.SEARCH;
    const newSearchParamsToUse = newSearchParams
      ? enhanceNewSearchParams({
          oldSearchParams,
          newSearchParams,
          newSearchType,
          newPageType: newPageTypeToUse,
          userId: userUid || '',
        })
      : oldSearchParams;
    const searchParamsNextPage = removeParameters(
      {
        ...newSearchParamsToUse,
        [URLParameters.PAGE_TYPE]: newPageTypeToUse,
        ...(highlightedHotel ? { [URLParameters.HIGHLIGHT_HOTEL_ID]: `${highlightedHotel}` } : {}),
      },
      createResultsParamsToDrop({
        highlightedHotel,
        isNewSearch,
        isChangeListView,
        isAlternativeDates,
        isBrowserBackNavigation,
      })
    );

    const urlNextPage = new URL(`${BASE_PATH}?${createQueryString(searchParamsNextPage)}`, window.location.href);
    if (shouldAddBrowserHistory) {
      addBrowserHistory(urlNextPage);
    } else {
      replaceBrowserHistory(urlNextPage);
    }

    setPageType(newPageTypeToUse);

    let pageWrapperDataToUse = pageWrapperData;
    if (shouldRefreshPageWrapperData({ isNewSearch, isBrowserBackNavigation, willRedoSearch })) {
      const pageWrapperDataPayload: PageWrapperDataPayload = {
        searchParams: searchParamsNextPage,
      };
      const pageWrapperDataUpdated = await clientAPICall<PageWrapperDataPayload, PageWrapperData>({
        apiName: API_NAME.PAGE_WRAPPER_DATA,
        payload: pageWrapperDataPayload,
        traceParent: searchInfoData.searchTraceParent,
        commonData,
      });

      if (isFalsyOrEmpty(pageWrapperDataUpdated)) {
        // PAGE_WRAPPER_DATA failed — the new search cannot proceed with stale data.
        // Revert the URL and page type to what they were before the attempt, and let
        // the user retry rather than firing events with a mismatched searchId/destination.
        const oldUrl = new URL(`${BASE_PATH}?${createQueryString(oldSearchParams)}`, window.location.href);
        replaceBrowserHistory(oldUrl);
        setPageType(oldSearchParams[URLParameters.PAGE_TYPE] as PAGE_TYPE);
        handleIsLoadingChanged(false);
        return;
      }

      pageWrapperDataToUse = pageWrapperDataUpdated;

      setPageWrapperData((prevState) => ({
        ...pageWrapperDataToUse,
        // The updatedServerHeaders are not send from the server to the client, use the previous values. These Headers shouldn't
        // be used on the client anyway.
        updatedServerHeaders: prevState.updatedServerHeaders,
      }));
    } else {
      pageWrapperDataToUse = {
        ...pageWrapperDataToUse,
        commonData: { ...pageWrapperDataToUse.commonData, pageStartTime: Date.now() },
      };
    }

    // Update the searchId in the url in case it changed coming from the BE in the new pageWrapperData object. This happens in
    // the local env as Casper changes the userId every time (In QA and Production the userId should always be the same)
    const updatedSearchId = pageWrapperDataToUse.searchInfoData.searchId;
    const searchIdsDifferent = updatedSearchId !== oldSearchParams[URLParameters.SEARCH_ID];
    const shouldUpdateSearchId = !!updatedSearchId && searchIdsDifferent;
    if (shouldUpdateSearchId) {
      clientAppLog({
        level: 'INFO',
        codeSourceName: 'pageWrapperClientSearch',
        logMessage: `REF999 searchId updated. updatedSearchId:${updatedSearchId}, oldSearchId:${oldSearchParams[URLParameters.SEARCH_ID]}`,
        url: window?.location?.href,
      });
      updateUrl({ queryParamsToSet: [{ [URLParameters.SEARCH_ID]: updatedSearchId }] });
    }

    if (refreshCSW) {
      startTransition(async () => {
        const updatedSearchDefaultData = await createSearchDefaultData({
          pageType: newPageTypeToUse,
          searchParams: searchParamsNextPage,
          commonData: pageWrapperDataToUse.commonData,
          staticData: pageWrapperData.staticData,
          localization: pageWrapperDataToUse.localization,
          curiosityParams: pageWrapperDataToUse.curiosityParams,
          translationDictionary,
          timings,
        });
        const updatedDefaultValues = createUpdatedDefaultValues(
          updatedSearchDefaultData.defaultValues,
          pageWrapperDataToUse.searchInfoDetailData,
          pageWrapperDataToUse.searchParams[URLParameters.TRIP_DATES]
        );
        setDefaultValuesCSW(updatedDefaultValues);
      });
    }

    if (willRedoSearch) {
      let searchPageDataResponse = {} as SearchPageData;
      const searchInfoDataToUse = pageWrapperDataToUse?.searchInfoData || searchInfoData;
      const searchInfoDetailDataToUse = pageWrapperDataToUse?.searchInfoDetailData || searchInfoDetailData;
      try {
        if (isNewSearch && myCallId === latestCallId) {
          fireSearchEventsBefore({
            searchParams: searchParamsNextPage,
            searchInfoData: searchInfoDataToUse,
            searchInfoDetailData: searchInfoDetailDataToUse,
            localization: pageWrapperDataToUse.localization,
            commonData: pageWrapperDataToUse.commonData,
            curiosityParams: pageWrapperDataToUse.curiosityParams,
          });
        }

        const searchPageDataPayload: SearchPageDataPayload = {
          searchParams: searchParamsNextPage,
        };
        searchPageDataResponse = await clientAPICall<SearchPageDataPayload, SearchPageData>({
          apiName: API_NAME.SEARCH_PAGE_DATA,
          payload: searchPageDataPayload,
          traceParent: searchInfoDataToUse.searchTraceParent,
          commonData,
        });

        if (!isFalsyOrEmpty(searchPageDataResponse)) {
          // The server builds the groups without the dynamic filters, which reads as every chip
          // being available, so keep the ones on screen while they still describe this search. They
          // are derived from the response, so the three move together or not at all.
          setSearchPageData((prevSearchPageData) =>
            prevSearchPageData.dynamicFilters?.searchKey === dynamicFiltersSearchKey(searchParamsNextPage)
              ? {
                  ...searchPageDataResponse,
                  dynamicFilters: prevSearchPageData.dynamicFilters,
                  filtersGroups: prevSearchPageData.filtersGroups,
                }
              : searchPageDataResponse
          );
        }

        if (searchPageDataResponse?.productsData?.apiStatus === APIStatusEnum.WAIT) {
          appendSearchLoadingStage('polling');
          const coordinates = { lat: searchPageData.lat, lon: searchPageData.lon };
          handleMapLoad({
            searchInfoDetailData,
            reloadType: ReloadType.SEARCH,
            mapRef,
            searchParams: searchParamsNextPage,
            dealCards: searchPageDataResponse?.productsData?.dealCards,
            coordinates,
            isMobile,
          });

          const searchParamsInitial = getSearchParams() as SearchParams;
          waitingCall({
            searchParamsInitial,
            commonData: pageWrapperDataToUse.commonData,
            localization: pageWrapperDataToUse.localization,
            handleWaitingFinished: handleWaitingFinishedIfCurrent(myCallId),
            setAPIStatus: handleSearchAPIStatusChanged,
            curiosityParams: pageWrapperDataToUse.curiosityParams,
            isNewSearch,
          });
        } else {
          handleWaitingFinishedIfCurrent(myCallId)({
            isNewSearch,
            searchParams: searchParamsNextPage,
            productsData: searchPageDataResponse?.productsData,
            searchInfoData: searchInfoDataToUse,
            searchInfoDetailData: searchInfoDetailDataToUse,
            commonDataToUse: pageWrapperDataToUse.commonData,
            localizationToUse: pageWrapperDataToUse.localization,
            curiosityParamsToUse: pageWrapperDataToUse.curiosityParams,
          });
        }
      } catch (e) {
        // Nothing on this path clears either loading flag, so the watchdog reports it as stuck.
        appendSearchLoadingStage('postResponseError');
        console.log('Error creating search page data', e);
      }
    }
  };

  const handleSearchChanged: HandleSearchChanged = async ({
    newSearchParams,
    shouldAddBrowserHistory = true,
    isNewSearch = false,
    isChangeListView = false,
    isAlternativeDates = false,
  }: HandleSearchChangedProps) => {
    await handleSearchChangedBase({
      newSearchParams,
      refreshSearch: true,
      refreshCSW: !!isAlternativeDates,
      shouldAddBrowserHistory,
      scrollPos: 0,
      isNewSearch,
      isChangeListView,
      isAlternativeDates,
    });
  };

  const handleNewSearch: HandleNewSearch = async (newSearchParams) => {
    // eslint-disable-next-line no-param-reassign
    scrollPosSearch.current = 0;
    setHotelId('');

    await handleSearchChangedBase({
      newSearchParams,
      isNewSearch: true,
      refreshSearch: true,
      shouldAddBrowserHistory: true,
      scrollPos: 0,
    });
  };

  const handleWaitingFinished = async ({
    searchParams,
    productsData,
    searchInfoData,
    searchInfoDetailData,
    isNewSearch,
    commonDataToUse,
    localizationToUse,
    curiosityParamsToUse,
  }: HandleWaitingFinishedParams) => {
    // Every client-side results-page search converges here, so this is where the data stops being the
    // reason the placeholders are up.
    watchSearchDataReady();

    if (productsData) {
      const { vcSearchId } = productsData;
      const searchParamsFresh = getSearchParams();
      const vcSearchIdShouldUpdate =
        vcSearchId !== '' ||
        (searchParamsFresh[URLParameters.VC_SEARCH_ID] && searchParamsFresh[URLParameters.VC_SEARCH_ID] !== vcSearchId);
      if (vcSearchIdShouldUpdate) {
        searchParamsFresh[URLParameters.VC_SEARCH_ID] = vcSearchId || '';
        window.history.replaceState(null, '', `?${createQueryString(searchParamsFresh)}`);
      }

      handleSearchProductsDataChanged(productsData);

      const coordinates = { lat: searchPageData.lat, lon: searchPageData.lon };
      handleMapLoad({
        searchInfoDetailData,
        reloadType: ReloadType.SEARCH,
        mapRef,
        searchParams,
        dealCards: productsData.dealCards,
        coordinates,
        isMobile,
      });
    }

    fireSearchEventsAfter({
      isNewSearch,
      searchParams,
      productsData: productsData || ({} as ProductsData),
      searchInfoData,
      searchInfoDetailData,
      defaultDestItems: searchDefaultData.defaultDestItems,
      commonData: commonDataToUse,
      localization: localizationToUse,
      curiosityParams: curiosityParamsToUse,
    });

    handleIsLoadingChanged(false);
  };

  // Superseded searches must stop before handleWaitingFinished, not part-way through it: everything in
  // there (watchdog, URL, products, loading flag) belongs to the search that is still current.
  // Defaults to the id current at arming time, which is what the initial-load poll needs: it has no
  // search of its own, so it is superseded by the first client search that starts after it.
  const handleWaitingFinishedIfCurrent =
    (myCallId: number = latestCallId) =>
    (params: HandleWaitingFinishedParams) => {
      if (myCallId !== latestCallId) return;
      handleWaitingFinished(params);
    };

  const handlePagination = async (newSearchParams: SearchParams) => {
    await handleSearchChanged({ newSearchParams });
  };

  const handleFiltersGroupsChanged = (newFiltersGroups: FiltersGroups) => {
    setSearchPageData((prevSearchPageData) => {
      const newSearchPageData: SearchPageData = {
        ...prevSearchPageData,
        filtersGroups: newFiltersGroups,
      };

      return newSearchPageData;
    });
  };

  const handleDynamicFiltersResponseChanged = ({
    newDynamicFiltersResponse,
    searchParams,
    searchInfoData,
    productsData,
  }: HandleDynamicFiltersResponseChangedProps) => {
    const updatedFiltersGroups = buildFilterGroups({
      searchParams,
      searchInfoData,
      loggedIn: false,
      commonData,
      localization,
      productsData,
      dynamicFiltersResponse: newDynamicFiltersResponse.filters,
      translationDictionary,
    });

    setSearchPageData((prevSearchPageData) => {
      const newSearchPageData: SearchPageData = {
        ...prevSearchPageData,
        // Stored even when the call failed: the bar then shows what it can rather than waiting for
        // a response that is not coming.
        dynamicFilters: { searchKey: dynamicFiltersSearchKey(searchParams), response: newDynamicFiltersResponse },
        filtersGroups: updatedFiltersGroups,
      };

      return newSearchPageData;
    });
  };

  const handleSearchProductsDataChanged: HandleSearchProductsDataChanged = (newProductsData: ProductsData) => {
    setSearchPageData((prevSearchPageData) => {
      const newSearchPageData: SearchPageData = {
        ...prevSearchPageData,
        productsData: newProductsData,
        isLoading: newProductsData.apiStatus === APIStatusEnum.WAIT,
      };

      return newSearchPageData;
    });
  };

  const handleSearchAPIStatusChanged = (newApiStatus: APIStatusEnum) => {
    setSearchPageData((prevSearchPageData) => {
      const newSearchPageData: SearchPageData = {
        ...prevSearchPageData,
        productsData: {
          ...prevSearchPageData.productsData,
          apiStatus: newApiStatus,
        },
        isLoading: newApiStatus === APIStatusEnum.WAIT,
      };

      return newSearchPageData;
    });
  };

  const handleIsLoadingChanged = (newIsLoading: boolean) => {
    setSearchPageData((prevSearchPageData) => {
      const newSearchPageData: SearchPageData = {
        ...prevSearchPageData,
        isLoading: newIsLoading,
      };

      return newSearchPageData;
    });
  };

  return {
    fireSearchEventsBefore,
    fireSearchEventsAfter,
    handleSearchChangedBase,
    handleSearchChanged,
    handleNewSearch,
    handleWaitingFinishedIfCurrent,
    handlePagination,
    handleFiltersGroupsChanged,
    handleDynamicFiltersResponseChanged,
    handleSearchProductsDataChanged,
    handleSearchAPIStatusChanged,
    handleIsLoadingChanged,
  };
};
